import Service, { inject as service } from '@ember/service';
import { PROJECTDIGEST_VERIFICATION_METHODS } from 'sh-www/models/projectdigest-verification';

/** Service for project digest related API calls. */
export default Service.extend({
  api: service(),
  errorTracker: service('error-tracker'),
  shData: service(),
  store: service(),

  /**
   * Creates a project digest.
   *
   * @param {User} user
   * @param {Object} data
   * @param {String} data.title
   * @param {String} data.link
   * @param {String} data.description
   * @param {Array<Object>} data.relationships.services.data
   * @param {String} data.videoCategory
   * @param {Array<String>} data.videoTopics
   *
   * @return {Ember.RSVP.Promise<ProjectDigest>}
   */
  createProjectDigest(user, data) {
    const userID = user.get('id');
    const includes = [
      'user',
      'services',
      'video-topics',
      'video-category',
      'application.buyer',
      'company',
    ];
    const endpoint = `/users/${userID}/projectdigests`;

    const options = { jsonData: data };
    this.shData.addIncludesToOptions(options, includes);
    options;

    return this.get('api').post(endpoint, options)
      .then((response) => {
        this.shData.pushResponseToStore(response);
        return this.get('store').peekRecord('projectdigest', response.data.id);
      });
  },

  /**
   * Deletes a given project digest.
   *
   * @param {ProjectDigest} projectDigest
   *
   * @return {Ember.RSVP.Promise}
   */
  deleteProjectDigest(projectDigest, deleteReview = false, includes = []) {
    const projectDigestId = projectDigest.id;
    const endpoint = `/projectdigests/${projectDigestId}`;
    const options = {
      params: {
        delete_review: deleteReview,
      },
    };

    this.shData.addIncludesToOptions(options, includes);

    return this.api.delete(endpoint, options).then((response) => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('projectdigest', response.data.id);
    });
  },

  /**
   * Restores a given project digest
   *
   * @param {ProjectDigest} projectDigest
   *
   * @return {Ember.RSVP.Promise}
   */
  restoreProjectDigest(projectDigest, includes = []) {
    const endpoint = `/projectdigests/${projectDigest.id}/restore`;
    const options = {};

    this.shData.addIncludesToOptions(options, includes);

    return this.api.put(endpoint, options).then((response) => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('projectdigest', response.data.id);
    });
  },

  setBadLink(projectdigestId, isLinkBad) {
    const options = {
      jsonData: {
        bad_link: isLinkBad,
      }
    }

    return this.api.post(`projectdigests/${projectdigestId}/set-bad-link`, options);
  },

  createVerification(projectdigest, data = {}) {
    const {
      method,
      minutes,
      seconds,
      link,
      referralFirstName,
      referralLastName,
      referralEmail,
      referralCompanyName,
      referralBrandId
    } = data;

    const verificationData = method === PROJECTDIGEST_VERIFICATION_METHODS.REFERRAL 
      ? {
          referral_firstname: referralFirstName,
          referral_lastname: referralLastName,
          referral_email: referralEmail,
          referral_company: referralCompanyName,
          referral_brand_id: referralBrandId
        }
      : { minutes, seconds, link };

    const options = {
      jsonData: {
        method,
        ...verificationData,
      },
    }

    return this.api.post(`projectdigests/${projectdigest.id}/verification`, options);
  },

  setVerificationStatus(projectdigestVerificationId, status) {
    const options = {
      jsonData: {
        status,
      },
    }
    return this.api.post(`projectdigests/verification/${projectdigestVerificationId}`, options);
  },

  /**
   * Updates a project digest.
   *
   * @param {User} user
   * @param {ProjectDigest} projectDigest
   * @param {Object} data
   * @param {String} data.title
   * @param {String} data.link
   * @param {String} data.description
   * @param {Array<Object>} data.relationships.services.data
   * @param {String} data.videoCategory
   * @param {Array<String>} data.videoTopics
   *
   * @return {Ember.RSVP.Promise<ProjectDigest>}
   */
  updateProjectDigest(user, projectDigest, data) {
    const userID = user.get('id');
    const projectDigestID = projectDigest.get('id');
    const includes = [
      'user.projectdigests',
      'services',
      'video-topics',
      'video-category',
      'application.buyer',
      'oembed',
      'company',
      'projectdigest-verifications:verified_or_latest',
      'classifications',
    ];
    const endpoint = `/users/${userID}/projectdigests/${projectDigestID}`;

    const options = { jsonData: data };
    this.shData.addIncludesToOptions(options, includes);

    return this.get('api').put(endpoint, options)
      .then((response) => {
        this.shData.pushResponseToStore(response);
        return this.get('store').peekRecord('projectdigest', response.data.id);
      });
  },

  getProjectDigest(projectDigestId, opt_includes = []) {
    const options = this.shData.addIncludesToOptions({}, opt_includes);
    return this.api.request(`projectdigests/${projectDigestId}`, options);
  },

  getProjectDigests(page, opt_includes = []) {
    const options = { params: { page } };
    this.shData.addIncludesToOptions(options, opt_includes);

    return this.api.request('projectdigests', options).then((response) => {
      this.shData.pushResponseToStore(response);
      return this.shData.getModelArrayFromResponse(response, 'projectdigest');
    });
  },

  /**
   * @param {ProjectDigest} projectDigest
   * @param {Boolean} verified
   * @return {Ember.RSVP.Promise}
   */
  setProjectDigestVerification(projectDigest, verified) {
    const endpoint = `/projectdigests/${projectDigest.id}/set-verification`;
    const options = {
      jsonData: {
        verified,
      },
    };

    return this.api.post(endpoint, options).then((response) => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('projectdigest', response.data.id);
    });
  },

  getProjectdigestToClassify(opt_includes = []) {
    const options = this.shData.addIncludesToOptions({}, opt_includes);

    return this.api.request('projectdigests/classification-queue', options).then((response) => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('projectdigest', response.data.id);
    });
  },

  updateProjectdigestClassifications(projectDigestId, classificationIds = []) {
    const options = {
      jsonData: {
        classificationIds: classificationIds.join(','),
      },
    };

    return this.api.put(`projectdigests/${projectDigestId}/classifications`, options);
  },

  updateProjectdigestFeaturedStatus(projectDigestId, featured) {
    const options = {
      params: {
        is_featured: featured
      },
    };

    return this.api.post(`projectdigests/${projectDigestId}/set-featured`, options);
  },
});
